import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePageContext } from "../../context/pageContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useToggle from "../../hooks/useToggle";
import { useI18n } from "../../hooks/useTranslations";
import usePackagingApi from "../../http/packaging/api";
import { Packaging, UpsertPackaging } from "../../http/packaging/types";
import { PaginationQuery } from "../../types";
import { formatPrice } from "../../utils";
import SvgAdd from "../icons/Add";
import SvgDelete from "../icons/Delete";
import SvgEdit from "../icons/Edit";
import Button from "../ui/Button";
import { Action } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import Sidebar from "../ui/Sidebar";
import Table from "../ui/Table";
import TH from "../ui/TH";
import PackagingForm, { PackagingFormBody } from "./PackagingForm";

const Packagings: FC = () => {
  const { usePackagings: getPackagings } = usePackagingApi();

  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const { isOpen, open, close } = useToggle();
  const [editPackaging, setEditPackaging] = useState<Packaging>();
  const [deletePackaging, setDeletePackaging] = useState<Packaging>();
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);
  const packagingApi = usePackagingApi();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);

  const pageContext = usePageContext();

  const query: PaginationQuery = { ...filters.state, q: debouncedValue };

  const { data, error } = getPackagings(query);

  useEffect(() => {
    error && dispatch(showErrorNotification(i18n, error));
  }, [error]);

  useEffect(() => {
    pageContext.setPage({ title: i18n.t.packagings.plural });
  }, [i18n.language]);

  const onSubmit = async (formData: PackagingFormBody) => {
    const body: UpsertPackaging = {
      code: formData.code,
      description: formData.description,
      cost: formData.cost,
    };
    if (!editPackaging) {
      try {
        await packagingApi.createPackaging(body);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    } else {
      try {
        packagingApi.updatePackaging(body, editPackaging.id);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    }
  };

  const actions: Action<Packaging>[] = [
    {
      title: i18n.t.common.edit,
      onClick: (item) => {
        setEditPackaging(item);
        open();
      },
      icon: SvgEdit,
    },
    {
      title: i18n.t.common.delete,
      onClick: setDeletePackaging,
      icon: SvgDelete,
      color: "red",
      isDivider: true,
    },
  ];

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  return (
    <>
      <Sidebar
        isOpen={isOpen}
        title={!editPackaging ? i18n.t.packagings.newPackaging : i18n.t.packagings.editPackaging}
        onClose={close}
      >
        <PackagingForm
          onSubmit={onSubmit}
          packaging={editPackaging}
          key={isOpen ? (editPackaging?.id ?? "new") : "closed"}
        />
      </Sidebar>

      <Table<Packaging>
        buttons={{
          main: (
            <Button
              buttonProps={{
                onClick: () => {
                  setEditPackaging(undefined);
                  open();
                },
              }}
              glyph={SvgAdd}
            />
          ),
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        rowActions={actions}
        onRowClick={(item) => {
          setEditPackaging(item);
          open();
        }}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.t.common.code}</th>
            <TH order={orderOptions} fieldName="packaging.description" title={i18n.t.common.description} />
            <TH order={orderOptions} fieldName="packaging.cost" title={i18n.t.common.cost} />
            <th align="left"></th>
          </tr>
        }
        renderRow={(item) => (
          <>
            <td>{item.code}</td>
            <td>{item.description}</td>
            <td>{formatPrice(item.cost, i18n.language, { min: 2, max: 4 })}</td>
          </>
        )}
        items={data?.items}
        total={data?.total}
        error={error}
      />
      {!!deletePackaging && (
        <ModalDialog
          onClose={() => setDeletePackaging(undefined)}
          title={i18n.t.packagings.delete}
          isSubmitting={isModalSubmitting}
          actions={[
            {
              title: i18n.t.common.cancel,
              type: "secondary",
              onClick: () => setDeletePackaging(undefined),
            },
            {
              title: i18n.t.common.yes,
              onClick: async () => {
                setIsModalSubmitting(true);
                if (!deletePackaging) return;
                try {
                  await packagingApi.deletePackaging(deletePackaging.id);
                  setIsModalSubmitting(false);
                  setDeletePackaging(undefined);
                } catch (e) {
                  setIsModalSubmitting(false);
                  dispatch(showErrorNotification(i18n, e));
                }
              },
            },
          ]}
        >
          {i18n.t.packagings.questions.delete}
        </ModalDialog>
      )}
    </>
  );
};

export default Packagings;
